import { FeedbackData, FeedbackFile } from '../types/feedback'

// Extended FeedbackFile interface to support File objects from FeedbackWidget
interface ExtendedFeedbackFile extends FeedbackFile {
  file?: File
}
import { config } from '../config/environment'

interface FeedbackApiSubmission {
  userId: string
  userEmail: string
  userDepartment: string
  toolName?: string
  message: string
  userAgent?: string
  platform: 'web' | 'desktop'
  attachments?: Array<{
    fileName: string
    fileType: string
    s3Key: string
  }>
  tags?: string[]
  sessionId?: string
  metadata?: Record<string, any>
}

interface FeedbackResponse {
  success: boolean
  data?: {
    submissionId: string
    timestamp: string
  }
  error?: string
}

/**
 * FeedbackService - Handles feedback submission to the backend API
 * Supports both web and desktop platforms with file attachments
 */
export class FeedbackService {
  private baseUrl: string

  constructor() {
    // Use config from environment
    this.baseUrl = config.API_BASE_URL
  }

  /**
   * Submit feedback with optional file attachments
   */
  async submitFeedback(feedbackData: FeedbackData): Promise<{ success: boolean; submissionId?: string; error?: string }> {
    try {
      console.log('Submitting feedback...')
      
      // Get user identity for submission
      const userIdentity = this.getUserIdentity()
      if (!userIdentity) {
        throw new Error('User identity not found. Please register first.')
      }

      const submissionData: FeedbackApiSubmission = {
        userId: userIdentity.userId,
        userEmail: userIdentity.email, 
        userDepartment: userIdentity.department,
        toolName: feedbackData.context.tool,
        message: feedbackData.feedback.text,
        userAgent: feedbackData.context.userAgent || navigator?.userAgent,
        platform: feedbackData.context.platform,
        sessionId: userIdentity.sessionId,
        metadata: feedbackData.context.additionalData
      }

      return await this.submitFeedbackInternal(submissionData, feedbackData.feedback.attachments as (ExtendedFeedbackFile | File)[])
    } catch (error) {
      console.error('Feedback submission error:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to submit feedback'
      }
    }
  }

  /**
   * Get user identity from storage
   */
  private getUserIdentity() {
    try {
      const stored = localStorage.getItem('reach3_user_identity')
      if (!stored) return null
      
      const identity = JSON.parse(stored)
      // Return properly formatted user identity
      return {
        userId: identity.id || identity.userId,
        email: identity.email,
        department: identity.department,
        sessionId: identity.sessionId || identity.id
      }
    } catch {
      return null
    }
  }

  /**
   * Internal method to submit feedback to API
   */
  private async submitFeedbackInternal(
    submissionData: FeedbackApiSubmission,
    attachments?: (ExtendedFeedbackFile | File)[]
  ): Promise<{ success: boolean; submissionId?: string; error?: string }> {
    const formData = new FormData()
    
    // Add feedback data
    formData.append('feedbackData', JSON.stringify(submissionData))
    
    // Add file attachments if any - handle both File objects and FeedbackFile objects
    if (attachments && attachments.length > 0) {
      attachments.forEach((attachment) => {
        try {
          // Check if it's a pure File object
          if (attachment instanceof File) {
            console.log(`Adding File attachment: ${attachment.name}, size: ${attachment.size}`)
            formData.append('attachments', attachment)
          }
          // Check if it's a FeedbackFile with a file property (from FeedbackWidget)
          else if ('file' in attachment && attachment.file instanceof File) {
            console.log(`Adding FeedbackFile with File object: ${attachment.name}, size: ${attachment.size}`)
            formData.append('attachments', attachment.file)
          }
          // Handle FeedbackFile with base64 data (legacy support)
          else if ('data' in attachment && attachment.data && attachment.data.length > 0) {
            let base64Data = attachment.data
            if (base64Data.includes(',')) {
              base64Data = base64Data.split(',')[1]
            }
            
            const byteCharacters = atob(base64Data)
            const byteNumbers = new Array(byteCharacters.length)
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i)
            }
            const byteArray = new Uint8Array(byteNumbers)
            const blob = new Blob([byteArray], { type: attachment.type })
            
            console.log(`Converting FeedbackFile: ${attachment.name}, original size: ${attachment.size}, blob size: ${blob.size}`)
            formData.append('attachments', blob, attachment.name)
          } else {
            console.warn('Attachment format not recognized:', attachment)
          }
        } catch (error) {
          console.error('Error processing attachment:', attachment, error)
        }
      })
    }

    const response = await fetch(`${this.baseUrl}/api/feedback`, {
      method: 'POST',
      credentials: 'include',
      body: formData
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Network error' }))
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`)
    }

    const result: FeedbackResponse = await response.json()
    
    if (!result.success) {
      throw new Error(result.error || 'Feedback submission failed')
    }

    return {
      success: true,
      submissionId: result.data?.submissionId
    }
  }
}

// Export singleton instance
export const feedbackService = new FeedbackService()